import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Delete, Camera, LogIn, LogOut, Coffee, Play, MapPin, ArrowLeft, CheckCircle2, X, RotateCcw, WifiOff, CloudUpload, UserPlus as UserPlusIcon, Repeat, Info } from "lucide-react";
import { api, apiError } from "../lib/api";
import { queuePunch, getQueue, syncQueue } from "../lib/offline";
import { printBadge } from "../lib/badge";
import { Logo } from "../components/Brand";
import { fmtDateTime, fmtTime, fmtDateStr, toUnit, UNIT_ABBR, UNIT_METERS } from "../lib/format";

function LiveClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="text-center">
      <div className="font-mono text-4xl sm:text-6xl font-bold tracking-wider text-slate-100" data-testid="live-clock">
        {now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
      </div>
      <div className="text-slate-400 text-sm mt-1">
        {now.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" })}
      </div>
    </div>
  );
}

function Selfie({ onCapture, onSkip, required }) {
  const videoRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [error, setError] = useState(false);
  const [shot, setShot] = useState(null);

  const startCam = useCallback(() => {
    navigator.mediaDevices
      ?.getUserMedia({ video: { facingMode: "user" } })
      .then((s) => {
        setStream(s);
        if (videoRef.current) videoRef.current.srcObject = s;
      })
      .catch(() => setError(true));
  }, []);

  useEffect(() => {
    startCam();
    return () => setStream((s) => { s?.getTracks().forEach((t) => t.stop()); return null; });
  }, [startCam]);

  const capture = () => {
    const v = videoRef.current;
    if (!v) return;
    const canvas = document.createElement("canvas");
    canvas.width = 480; canvas.height = 360;
    const ctx = canvas.getContext("2d");
    ctx.translate(canvas.width, 0); ctx.scale(-1, 1);
    ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
    setShot(canvas.toDataURL("image/jpeg", 0.8));
    stream?.getTracks().forEach((t) => t.stop());
  };

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="relative w-full max-w-sm aspect-[4/3] rounded-2xl overflow-hidden border-2 border-blue-500/40 bg-slate-950 shadow-[0_0_25px_rgba(29,84,196,0.25)]">
        {shot ? (
          <img src={shot} alt="selfie" className="w-full h-full object-cover" data-testid="kiosk-selfie-preview" />
        ) : error ? (
          <div className="w-full h-full flex flex-col items-center justify-center text-slate-500 gap-2 p-4 text-center">
            <Camera className="w-8 h-8" /><span className="text-sm">Camera unavailable</span>
          </div>
        ) : (
          <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover -scale-x-100" />
        )}
        <div className="absolute inset-4 border border-white/20 rounded-xl pointer-events-none" />
      </div>
      {shot ? (
        <div className="flex gap-3 w-full max-w-sm">
          <button onClick={() => { setShot(null); startCam(); }} data-testid="selfie-retake-button"
            className="flex-1 rounded-xl bg-slate-800 hover:bg-slate-700 py-3 font-semibold flex items-center justify-center gap-2 transition-colors">
            <RotateCcw className="w-4 h-4" /> Retake
          </button>
          <button onClick={() => onCapture(shot)} data-testid="selfie-confirm-button"
            className="flex-1 rounded-xl bg-blue-600 hover:bg-blue-500 py-3 font-semibold flex items-center justify-center gap-2 transition-colors">
            <CheckCircle2 className="w-4 h-4" /> Use photo
          </button>
        </div>
      ) : (
        <div className="flex gap-3 w-full max-w-sm">
          {(!required || error) && (
            <button onClick={onSkip} data-testid="selfie-skip-button" className="flex-1 rounded-xl bg-slate-800 hover:bg-slate-700 py-3 font-semibold transition-colors">
              {error ? "Continue" : "Skip"}
            </button>
          )}
          {!error && (
            <button onClick={capture} data-testid="selfie-capture-button" className="flex-1 rounded-xl bg-blue-600 hover:bg-blue-500 py-3 font-semibold flex items-center justify-center gap-2 transition-colors">
              <Camera className="w-4 h-4" /> Capture
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function firstStep(c, isMobile) {
  if (!c) return "company";
  if (c.kiosk_mode === "visitors") return "visitor";
  if (c.kiosk_mode === "clock") return "pin";
  return isMobile ? "pin" : "choose";
}

const ACTION_META = {
  in: { label: "Clock In", icon: LogIn, color: "bg-blue-600 hover:bg-blue-500", testid: "kiosk-clock-in-button" },
  out: { label: "Clock Out", icon: LogOut, color: "bg-rose-600 hover:bg-rose-500", testid: "kiosk-clock-out-button" },
  break_start: { label: "Start Break", icon: Coffee, color: "bg-amber-600 hover:bg-amber-500", testid: "kiosk-break-button" },
  break_end: { label: "End Break", icon: Play, color: "bg-emerald-600 hover:bg-emerald-500", testid: "kiosk-break-end-button" },
};
const ACTIONS_BY_STATE = { out: ["in"], in: ["out", "break_start"], break: ["break_end"] };

export default function ClockScreen({ mode, fixedCompanyId }) {
  const isMobile = mode === "mobile";
  const [companies, setCompanies] = useState([]);
  const [company, setCompany] = useState(null);
  const [pin, setPin] = useState("");
  const [employee, setEmployee] = useState(null);
  const [step, setStep] = useState(fixedCompanyId ? "loading" : "company");
  const [pendingAction, setPendingAction] = useState(null);
  const [geo, setGeo] = useState(null);
  const [geoError, setGeoError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [queued, setQueued] = useState(getQueue().length);
  const [lastOffline, setLastOffline] = useState(false);
  const [lastAction, setLastAction] = useState(null);
  const [visitor, setVisitor] = useState({ name: "", organization: "", host: "", purpose: "", phone: "" });
  const [hosts, setHosts] = useState({ employees: [], organizations: [] });
  const [selectedRate, setSelectedRate] = useState(null);
  const [myts, setMyts] = useState(null);
  const [swap, setSwap] = useState(null); // { shifts, coworkers }
  const [swapShift, setSwapShift] = useState("");
  const [swapTarget, setSwapTarget] = useState("");
  const [lockOpen, setLockOpen] = useState(false);
  const [lockPin, setLockPin] = useState("");
  const [lockSecs, setLockSecs] = useState(0);
  const [permHint, setPermHint] = useState(true);
  const [camError, setCamError] = useState(false);
  const lockTimer = useRef(null);
  const navigate = useNavigate();
  const startLock = (secs) => {
    setLockSecs(secs);
    clearInterval(lockTimer.current);
    lockTimer.current = setInterval(() => setLockSecs((s) => { if (s <= 1) { clearInterval(lockTimer.current); return 0; } return s - 1; }), 1000);
  };
  useEffect(() => () => clearInterval(lockTimer.current), []);

  const captureGeo = useCallback((c) => {
    if (!navigator.geolocation) { setGeoError("Geolocation not supported"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        let distance = null, inside = true, unit = c.distance_unit || "meters";
        if (c.geofence_lat != null && c.geofence_lng != null) {
          const R = 6371000, toRad = (d) => (d * Math.PI) / 180;
          const dLat = toRad(c.geofence_lat - latitude), dLng = toRad(c.geofence_lng - longitude);
          const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(latitude)) * Math.cos(toRad(c.geofence_lat)) * Math.sin(dLng / 2) ** 2;
          const meters = 2 * R * Math.asin(Math.sqrt(a));
          distance = toUnit(meters, unit);
          inside = meters <= (c.geofence_radius || 150) * (UNIT_METERS[unit] || 1);
        }
        setGeo({ lat: latitude, lng: longitude, distance, inside, unit });
        setGeoError(null);
      },
      () => setGeoError("Location permission denied"),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  const selectCompany = useCallback((c) => {
    setCompany(c);
    setStep(firstStep(c, isMobile));
    if (isMobile) captureGeo(c);
    if (isMobile && c.require_selfie) {
      setCamError(false);
      navigator.mediaDevices?.getUserMedia({ video: { facingMode: "user" } })
        .then((s) => { s.getTracks().forEach((t) => t.stop()); setCamError(false); })
        .catch(() => setCamError(true));
    }
    api.get(`/public/companies/${c.id}/hosts`).then(({ data }) => setHosts(data)).catch(() => {});
  }, [isMobile, captureGeo]);

  // load companies (or fixed company from per-company URL)
  useEffect(() => {
    if (fixedCompanyId) {
      api.get(`/public/companies/${fixedCompanyId}`)
        .then(({ data }) => selectCompany(data))
        .catch(() => { setStep("notfound"); toast.error("Company not found"); });
      return;
    }
    api.get("/public/companies").then(({ data }) => {
      setCompanies(data);
      if (data.length === 1) selectCompany(data[0]);
    }).catch(() => {});
  }, [fixedCompanyId, selectCompany]);

  useEffect(() => {
    if (company) document.documentElement.style.setProperty("--tenant-accent", company.primary_color);
  }, [company]);

  // online/offline listeners + auto-sync
  useEffect(() => {
    const goOnline = async () => {
      setOnline(true);
      const n = await syncQueue(api);
      setQueued(getQueue().length);
      if (n > 0) toast.success(`${n} offline punch${n > 1 ? "es" : ""} synced`);
    };
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    if (navigator.onLine) syncQueue(api).then(() => setQueued(getQueue().length));
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); };
  }, []);

  const pressKey = (k) => { if (lockSecs > 0) return; if (pin.length < 8) setPin((p) => p + k); };

  const verify = useCallback(async () => {
    if (pin.length < 4) { toast.error("Enter your PIN"); return; }
    if (!navigator.onLine) {
      // offline: can't verify PIN with server, go straight to action choice
      setEmployee({ employee_id: null, name: "Employee", state: "out" });
      setStep("actions");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/clock/verify", { company_id: company.id, pin });
      setEmployee(data);
      setSelectedRate(data.rates && data.rates.length ? data.rates[0].label : null);
      setStep("actions");
    } catch (err) {
      if (err.response?.status === 429) {
        const m = /(\d+)\s*s/.exec(err.response?.data?.detail || "");
        startLock(m ? parseInt(m[1], 10) : parseInt(err.response.headers?.["retry-after"] || "60", 10));
        toast.error("Too many attempts. Please wait.");
      } else {
        toast.error(apiError(err.response?.data?.detail) || "Invalid PIN");
      }
      setPin("");
    } finally { setBusy(false); }
  }, [pin, company]);

  const chooseAction = (action) => {
    setPendingAction(action);
    if (company.require_selfie) setStep("selfie");
    else submitClock(action, null);
  };

  // Hardware / on-screen keyboard support for the PIN step (iPad, Android tablets).
  useEffect(() => {
    if (step !== "pin") return undefined;
    const onKey = (e) => {
      if (lockSecs > 0) return;
      if (e.key >= "0" && e.key <= "9") { if (pin.length < 8) setPin((p) => p + e.key); }
      else if (e.key === "Backspace") setPin((p) => p.slice(0, -1));
      else if (e.key === "Enter") verify();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [step, pin, verify, lockSecs]);

  const submitClock = async (action, photo) => {
    setLastAction(action);
    const payload = {
      company_id: company.id, pin, action, source: mode,
      lat: geo?.lat ?? null, lng: geo?.lng ?? null, photo_data: photo,
      rate_label: selectedRate,
      client_timestamp: new Date().toISOString(),
    };
    // offline path
    if (!navigator.onLine) {
      queuePunch(payload);
      setQueued(getQueue().length);
      setLastOffline(true);
      setStep("done");
      setTimeout(reset, 3500);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/clock", payload);
      setEmployee((e) => ({ ...e, state: data.state }));
      setLastOffline(false);
      setStep("done");
      toast.success(`${data.employee_name} — ${ACTION_META[action].label} recorded`);
      setTimeout(reset, 3500);
    } catch (err) {
      if (!err.response) {
        // network dropped mid-request -> queue
        queuePunch(payload);
        setQueued(getQueue().length);
        setLastOffline(true);
        setStep("done");
        setTimeout(reset, 3500);
      } else {
        toast.error(apiError(err.response?.data?.detail) || "Failed");
        setStep("actions");
      }
    } finally { setBusy(false); setPendingAction(null); }
  };

  const reset = () => {
    setPin(""); setEmployee(null); setPendingAction(null); setLastOffline(false); setLastAction(null);
    const base = company ? firstStep(company, isMobile) : "pin";
    if (company && (fixedCompanyId || companies.length === 1 || isMobile)) setStep(base);
    else { setStep("company"); if (!fixedCompanyId) setCompany(null); }
  };

  const handleExit = () => {
    if (!isMobile && company?.kiosk_exit_lock) { setLockPin(""); setLockOpen(true); }
    else navigate("/");
  };
  const submitExit = async () => {
    try {
      const { data } = await api.post("/public/kiosk/exit-verify", { company_id: company.id, pin: lockPin });
      if (data.ok) navigate("/");
      else { toast.error("Wrong exit PIN"); setLockPin(""); }
    } catch (err) {
      if (err.response?.status === 429) {
        const m = /(\d+)\s*s/.exec(err.response?.data?.detail || "");
        startLock(m ? parseInt(m[1], 10) : 60);
        setLockPin("");
      } else toast.error("Could not verify PIN");
    }
  };

  const startVisitor = () => {
    if (!visitor.name.trim()) { toast.error("Please enter your name"); return; }
    if (company.require_visitor_photo) setStep("visitor_photo");
    else submitVisitor(null);
  };

  const submitVisitor = async (photo) => {
    if (!visitor.name.trim()) { toast.error("Please enter your name"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/public/visitors", { company_id: company.id, ...visitor, photo_data: photo || null });
      if (company.badge_printing_enabled !== false) { printBadge(data.visitor, data.company); toast.success("Welcome! Your badge is printing."); }
      else toast.success("Welcome! You're checked in.");
      setVisitor({ name: "", organization: "", host: "", purpose: "", phone: "" });
      reset();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  const openTimesheet = async () => {
    try { const { data } = await api.post("/employee/timesheet", { company_id: company.id, pin }); setMyts(data); setStep("timesheet"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail) || "Failed"); }
  };
  const approveTs = async () => {
    try { await api.post("/employee/approve", { company_id: company.id, pin }); toast.success("Hours approved — thank you!"); reset(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const reportTs = async () => {
    const msg = window.prompt("Describe the discrepancy for your manager:");
    if (!msg) return;
    try { await api.post("/employee/correction", { company_id: company.id, pin, message: msg }); toast.success("Sent to your manager"); reset(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const openSwap = async () => {
    try {
      const { data } = await api.post("/employee/schedules", { company_id: company.id, pin });
      if (!data.shifts.length) { toast.error("You have no upcoming shifts to swap"); return; }
      setSwap(data); setSwapShift(data.shifts[0]?.id || ""); setSwapTarget(""); setStep("swap");
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Failed"); }
  };
  const submitSwap = async () => {
    if (!swapShift || !swapTarget) { toast.error("Pick a shift and a coworker"); return; }
    try {
      await api.post("/employee/swap-request", { company_id: company.id, pin, schedule_id: swapShift, target_employee_id: swapTarget });
      toast.success("Swap request sent to your manager"); reset();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Failed"); }
  };

  const accent = company?.primary_color || "#1d54c4";

  return (
    <div style={company?.kiosk_bg_color ? { backgroundColor: company.kiosk_bg_color } : undefined} className={`min-h-screen bg-slate-950 text-slate-100 flex flex-col ${isMobile ? "max-w-md mx-auto" : ""}`}>
      <header className="flex items-center justify-between p-4 sm:p-6 border-b border-slate-900">
        <Logo size="sm" logoPath={company?.logo_path} companyName={company?.name} showText={!!company} />
        <div className="flex items-center gap-3">
          {!online && (
            <span data-testid="offline-badge" className="inline-flex items-center gap-1.5 text-xs font-mono text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-full px-2.5 py-1">
              <WifiOff className="w-3.5 h-3.5" /> Offline
            </span>
          )}
          {queued > 0 && (
            <span data-testid="queued-badge" className="inline-flex items-center gap-1.5 text-xs font-mono text-blue-300 bg-blue-500/10 border border-blue-500/30 rounded-full px-2.5 py-1">
              <CloudUpload className="w-3.5 h-3.5" /> {queued} pending
            </span>
          )}
          <button onClick={handleExit} data-testid="clock-exit-link" className="text-slate-500 hover:text-slate-300 transition-colors"><ArrowLeft className="w-5 h-5" /></button>
        </div>
      </header>

      <div className="flex-1 flex flex-col items-center justify-center p-4 sm:p-8 w-full">
        {!isMobile && company && step !== "company" && step !== "done" && (
          <div className="mb-5 flex justify-center" data-testid="kiosk-center-logo"><Logo size="xl" logoPath={company.logo_path} companyName={company.name} /></div>
        )}
        {step !== "company" && step !== "done" && <div className="mb-6"><LiveClock /></div>}

        {isMobile && company && step !== "company" && step !== "done" && (
          <div data-testid="mobile-gps-status-badge" className={`mb-6 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm border ${
            geoError ? "bg-rose-500/10 border-rose-500/30 text-rose-300" :
            !geo ? "bg-slate-800 border-slate-700 text-slate-300" :
            geo.inside ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300" : "bg-amber-500/10 border-amber-500/30 text-amber-300"}`}>
            <MapPin className="w-4 h-4" />
            {geoError ? geoError : !geo ? "Locating…" : geo.distance == null ? "Location captured" :
             geo.inside ? `Inside work zone · ${geo.distance}${UNIT_ABBR[geo.unit] || "m"}` : `${geo.distance}${UNIT_ABBR[geo.unit] || "m"} away · outside zone`}
          </div>
        )}

        {isMobile && company && company.require_selfie && camError && step !== "company" && step !== "done" && (
          <div data-testid="mobile-camera-status-badge" className="mb-6 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm border bg-rose-500/10 border-rose-500/30 text-rose-300">
            <Camera className="w-4 h-4" />
            Camera permission denied
          </div>
        )}

        {isMobile && company && permHint && step !== "company" && step !== "done" && (
          <div data-testid="mobile-permissions-hint" className="mb-4 w-full max-w-sm flex items-start gap-2 rounded-xl bg-blue-500/10 border border-blue-500/30 text-blue-200 px-4 py-3 text-sm">
            <Info className="w-4 h-4 mt-0.5 shrink-0" />
            <span className="flex-1">Please allow <b>Location</b>{company.require_selfie && <> and <b>Camera</b></>} access when your phone asks — {company.require_selfie ? "both are" : "it's"} needed to clock in.</span>
            <button data-testid="dismiss-permissions-hint" onClick={() => setPermHint(false)} className="text-blue-300/70 hover:text-blue-100 transition-colors"><X className="w-4 h-4" /></button>
          </div>
        )}

        <AnimatePresence mode="wait">
          {step === "loading" && (
            <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-center py-16" data-testid="clock-loading">
              <div className="animate-pulse font-mono text-slate-400">Loading…</div>
            </motion.div>
          )}

          {step === "notfound" && (
            <motion.div key="notfound" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-center py-16 max-w-md" data-testid="clock-notfound">
              <h2 className="text-2xl font-bold mb-2">Company not found</h2>
              <p className="text-slate-400 text-sm">This clock link is invalid or has been removed. Please contact your administrator for the correct link.</p>
            </motion.div>
          )}

          {step === "company" && !fixedCompanyId && (
            <motion.div key="company" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="w-full max-w-md">
              <h2 className="text-2xl font-bold text-center mb-2">Select your company</h2>
              <p className="text-slate-400 text-center text-sm mb-6">Choose the workplace to clock in</p>
              <div className="space-y-3">
                {companies.map((c) => (
                  <button key={c.id} data-testid={`clock-company-${c.id}`} onClick={() => selectCompany(c)}
                    className="w-full flex items-center gap-4 bg-slate-900 hover:bg-slate-800 border border-slate-800 hover:border-blue-500/40 rounded-2xl p-4 transition-colors text-left">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white font-bold shrink-0" style={{ backgroundColor: c.primary_color }}>{c.name[0]}</div>
                    <span className="font-semibold">{c.name}</span>
                  </button>
                ))}
                {companies.length === 0 && <p className="text-center text-slate-500 text-sm">No companies yet.</p>}
              </div>
            </motion.div>
          )}

          {step === "choose" && (
            <motion.div key="choose" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="w-full max-w-2xl grid grid-cols-1 sm:grid-cols-2 gap-5">
              <button data-testid="choose-employee-button" onClick={() => setStep("pin")}
                className="h-56 rounded-3xl text-white font-extrabold text-2xl flex flex-col items-center justify-center gap-4 transition-transform active:scale-95 shadow-xl"
                style={{ backgroundColor: company?.employee_button_color || "#1d54c4" }}>
                <LogIn className="w-14 h-14" /> Employee Clock In
              </button>
              <button data-testid="choose-visitor-button" onClick={() => setStep("visitor")}
                className="h-56 rounded-3xl text-white font-extrabold text-2xl flex flex-col items-center justify-center gap-4 transition-transform active:scale-95 shadow-xl"
                style={{ backgroundColor: company?.visitor_button_color || "#0891b2" }}>
                <UserPlusIcon className="w-14 h-14" /> Visitors Check In
              </button>
            </motion.div>
          )}

          {step === "pin" && (
            <motion.div key="pin" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="w-full max-w-xs">
              <div className="flex items-center justify-center gap-3 mb-8" data-testid="kiosk-pin-display">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className={`w-4 h-4 rounded-full transition-all ${i < pin.length ? "scale-110" : "bg-slate-700"}`}
                    style={{ backgroundColor: i < pin.length ? accent : undefined }} />
                ))}
                {pin.length > 4 && <span className="font-mono text-blue-400">+{pin.length - 4}</span>}
              </div>
              {lockSecs > 0 && (
                <div data-testid="pin-lock-banner" className="mb-4 text-center text-sm font-semibold text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-xl py-2.5">
                  Too many attempts — try again in {lockSecs}s
                </div>
              )}
              <div className="grid grid-cols-3 gap-3 sm:gap-4">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                  <button key={n} data-testid={`kiosk-pin-key-${n}`} onClick={() => pressKey(String(n))}
                    className="h-16 sm:h-20 text-2xl sm:text-3xl font-bold rounded-2xl bg-slate-800/80 hover:bg-slate-700 active:scale-95 transition-all border border-slate-700/60 shadow-lg">{n}</button>
                ))}
                <button onClick={() => setPin("")} data-testid="kiosk-pin-clear" className="h-16 sm:h-20 rounded-2xl bg-slate-900 hover:bg-slate-800 text-slate-400 text-sm font-semibold border border-slate-800 transition-colors">Clear</button>
                <button data-testid="kiosk-pin-key-0" onClick={() => pressKey("0")} className="h-16 sm:h-20 text-2xl sm:text-3xl font-bold rounded-2xl bg-slate-800/80 hover:bg-slate-700 active:scale-95 transition-all border border-slate-700/60 shadow-lg">0</button>
                <button onClick={() => setPin((p) => p.slice(0, -1))} data-testid="kiosk-pin-backspace" className="h-16 sm:h-20 rounded-2xl bg-slate-900 hover:bg-slate-800 text-slate-400 flex items-center justify-center border border-slate-800 transition-colors"><Delete className="w-6 h-6" /></button>
              </div>
              <button onClick={verify} disabled={busy || lockSecs > 0} data-testid="kiosk-pin-submit"
                className="mt-6 w-full h-14 rounded-2xl text-white font-bold text-lg disabled:opacity-60 transition-transform active:scale-95" style={{ backgroundColor: accent }}>
                {lockSecs > 0 ? `Locked — ${lockSecs}s` : busy ? "Checking…" : "Continue"}
              </button>
              {company?.kiosk_mode !== "clock" && !isMobile && (
                <button onClick={() => setStep("visitor")} data-testid="visitor-signin-button" className="mt-3 w-full text-sm text-slate-400 hover:text-slate-200 transition-colors">
                  I'm a visitor &rarr;
                </button>
              )}
            </motion.div>
          )}

          {step === "actions" && employee && (
            <motion.div key="actions" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="w-full max-w-sm cs-pop">
              <div className="text-center mb-8">
                <p className="text-slate-400 text-sm">Hello,</p>
                <h2 className="text-3xl font-extrabold" data-testid="clock-employee-name">{employee.name}</h2>
                {!online ? (
                  <span className="inline-block mt-2 text-xs font-mono uppercase tracking-wider px-3 py-1 rounded-full border border-amber-500/30 text-amber-300">Offline — punch will sync later</span>
                ) : (
                  <span className="inline-block mt-2 text-xs font-mono uppercase tracking-wider px-3 py-1 rounded-full border border-slate-700 text-slate-300">
                    Currently: {employee.state === "in" ? "On the clock" : employee.state === "break" ? "On break" : "Clocked out"}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-1 gap-3">
                {employee.rates && employee.rates.length > 1 && (
                  <div className="mb-1 text-left" data-testid="rate-selector">
                    <label className="text-xs text-slate-400 uppercase tracking-wider">Pay rate for this shift</label>
                    <select value={selectedRate || ""} onChange={(e) => setSelectedRate(e.target.value)} data-testid="rate-select"
                      className="mt-1.5 w-full rounded-xl bg-slate-950 border border-slate-700 px-4 py-3 focus:border-blue-500 outline-none transition">
                      {employee.rates.map((r) => <option key={r.label} value={r.label}>{r.label} — {r.rate}/hr</option>)}
                    </select>
                  </div>
                )}
                {(online ? ACTIONS_BY_STATE[employee.state] : ["in", "out", "break_start", "break_end"]).map((a) => {
                  const m = ACTION_META[a];
                  return (
                    <button key={a} data-testid={m.testid} onClick={() => chooseAction(a)} disabled={busy}
                      className={`h-16 rounded-2xl text-white font-bold text-lg flex items-center justify-center gap-3 transition-transform active:scale-95 ${m.color}`}>
                      <m.icon className="w-6 h-6" /> {m.label}
                    </button>
                  );
                })}
                <button onClick={openTimesheet} data-testid="my-timesheet-button" className="h-12 rounded-2xl bg-slate-800 hover:bg-slate-700 text-slate-200 font-semibold border border-slate-700 flex items-center justify-center gap-2 transition-colors">
                  My timesheet
                </button>
                {employee.employee_id && (
                  <button onClick={openSwap} data-testid="request-swap-button" className="h-12 rounded-2xl bg-slate-800 hover:bg-slate-700 text-slate-200 font-semibold border border-slate-700 flex items-center justify-center gap-2 transition-colors">
                    <Repeat className="w-4 h-4" /> Request shift swap
                  </button>
                )}
                <button onClick={reset} data-testid="clock-cancel-button" className="h-12 rounded-2xl bg-slate-900 hover:bg-slate-800 text-slate-400 font-semibold border border-slate-800 flex items-center justify-center gap-2 transition-colors">
                  <X className="w-4 h-4" /> Cancel
                </button>
              </div>
            </motion.div>
          )}

          {step === "selfie" && (
            <motion.div key="selfie" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="w-full max-w-sm">
              <h2 className="text-xl font-bold text-center mb-1">Verify it's you</h2>
              <p className="text-slate-400 text-center text-sm mb-6">{ACTION_META[pendingAction].label} for {employee.name}</p>
              <Selfie required onCapture={(d) => submitClock(pendingAction, d)} onSkip={() => submitClock(pendingAction, null)} />
            </motion.div>
          )}

          {step === "visitor" && (
            <motion.div key="visitor" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="w-full max-w-sm">
              <h2 className="text-xl font-bold text-center mb-1">Visitor sign-in</h2>
              <p className="text-slate-400 text-center text-sm mb-6">We'll print your badge</p>
              <div className="space-y-3">
                <input data-testid="visitor-field-name" placeholder="Full name *" value={visitor.name}
                  onChange={(e) => setVisitor((v) => ({ ...v, name: e.target.value }))}
                  className="w-full rounded-xl bg-slate-950 border border-slate-700 px-4 py-3 focus:border-blue-500 outline-none transition" />
                <input data-testid="visitor-field-organization" placeholder="Organization" value={visitor.organization}
                  onChange={(e) => setVisitor((v) => ({ ...v, organization: e.target.value }))}
                  className="w-full rounded-xl bg-slate-950 border border-slate-700 px-4 py-3 focus:border-blue-500 outline-none transition" />
                {company?.visit_types?.length > 0 && (
                  <select data-testid="visitor-field-visittype" value={visitor.visit_type || ""}
                    onChange={(e) => setVisitor((v) => ({ ...v, visit_type: e.target.value }))}
                    className="w-full rounded-xl bg-slate-950 border border-slate-700 px-4 py-3 focus:border-blue-500 outline-none transition">
                    <option value="">Type of visit…</option>
                    {company.visit_types.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                )}
                <select data-testid="visitor-field-host" value={visitor.host}
                  onChange={(e) => setVisitor((v) => ({ ...v, host: e.target.value }))}
                  className="w-full rounded-xl bg-slate-950 border border-slate-700 px-4 py-3 focus:border-blue-500 outline-none transition">
                  <option value="">Who are you visiting?</option>
                  {hosts.employees.length > 0 && <optgroup label="Employees">{hosts.employees.map((h) => <option key={h.name} value={h.name}>{h.name}{h.title ? ` — ${h.title}` : ""}{h.organization ? ` (${h.organization})` : ""}</option>)}</optgroup>}
                  {hosts.organizations.length > 0 && <optgroup label="Departments / Organizations">{hosts.organizations.map((h) => <option key={h} value={h}>{h}</option>)}</optgroup>}
                </select>
                <textarea data-testid="visitor-field-purpose" placeholder="Reason for your visit" value={visitor.purpose} rows={2}
                  onChange={(e) => setVisitor((v) => ({ ...v, purpose: e.target.value }))}
                  className="w-full rounded-xl bg-slate-950 border border-slate-700 px-4 py-3 focus:border-blue-500 outline-none transition resize-none" />
              </div>
              <button onClick={startVisitor} disabled={busy} data-testid="visitor-submit-button"
                className="mt-6 w-full h-14 rounded-2xl text-white font-bold text-lg disabled:opacity-60 transition-transform active:scale-95" style={{ backgroundColor: accent }}>
                {busy ? "Signing in…" : "Sign in & print badge"}
              </button>
              <button onClick={reset} data-testid="visitor-cancel-button" className="mt-3 w-full text-sm text-slate-400 hover:text-slate-200 transition-colors">Cancel</button>
            </motion.div>
          )}

          {step === "visitor_photo" && (
            <motion.div key="vphoto" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="w-full max-w-sm">
              <h2 className="text-xl font-bold text-center mb-1">Take a photo</h2>
              <p className="text-slate-400 text-center text-sm mb-6">For {visitor.name}'s visitor badge</p>
              <Selfie required onCapture={(d) => submitVisitor(d)} onSkip={() => submitVisitor(null)} />
            </motion.div>
          )}

          {step === "timesheet" && myts && (
            <motion.div key="timesheet" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="w-full max-w-lg">
              <h2 className="text-xl font-bold text-center mb-1">{myts.employee.name} — my hours</h2>
              <p className="text-slate-400 text-center text-sm mb-4">{fmtDateStr(myts.from)} → {fmtDateStr(myts.to)}</p>
              <div className="bg-slate-900/80 border border-slate-800 rounded-2xl overflow-hidden max-h-64 overflow-y-auto">
                {myts.shifts.length === 0 && <p className="text-center text-slate-500 text-sm py-6">No shifts in this period.</p>}
                {myts.shifts.map((s, i) => (
                  <div key={i} className="flex items-center justify-between px-4 py-2.5 border-b border-slate-800/60 text-sm">
                    <div className="flex flex-col">
                      <span className="text-slate-300" data-testid={`ts-shift-in-${i}`}>In: {fmtDateTime(s.start)}</span>
                      <span className="text-xs text-slate-500" data-testid={`ts-shift-out-${i}`}>Out: {s.open ? "active" : fmtTime(s.end)}</span>
                    </div>
                    <span className="font-mono">{Math.floor(s.worked_seconds / 3600)}h {Math.floor((s.worked_seconds % 3600) / 60)}m</span>
                  </div>
                ))}
              </div>
              <div className="text-center my-4 font-mono text-lg">Total: {Math.floor(myts.total_seconds / 3600)}h {Math.floor((myts.total_seconds % 3600) / 60)}m</div>
              {myts.approval.admin_approved ? (
                <div className="text-center text-emerald-400 font-semibold" data-testid="ts-approved-badge">✓ Approved for payroll</div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={reportTs} data-testid="ts-report-button" className="h-14 rounded-2xl bg-amber-600 hover:bg-amber-500 text-white font-bold transition-transform active:scale-95">Report an issue</button>
                  <button onClick={approveTs} data-testid="ts-approve-button" className="h-14 rounded-2xl text-white font-bold transition-transform active:scale-95" style={{ backgroundColor: accent }}>
                    {myts.approval.employee_approved ? "Re-approve" : "Approve hours"}
                  </button>
                </div>
              )}
              <button onClick={reset} className="mt-3 w-full text-sm text-slate-400 hover:text-slate-200 transition-colors">Back</button>
            </motion.div>
          )}

          {step === "swap" && swap && (
            <motion.div key="swap" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="w-full max-w-sm">
              <h2 className="text-xl font-bold text-center mb-1">Request a shift swap</h2>
              <p className="text-slate-400 text-center text-sm mb-6">Your manager approves all swaps</p>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-slate-400 uppercase tracking-wider">Which shift?</label>
                  <select data-testid="swap-shift-select" value={swapShift} onChange={(e) => setSwapShift(e.target.value)}
                    className="mt-1.5 w-full rounded-xl bg-slate-950 border border-slate-700 px-4 py-3 focus:border-blue-500 outline-none transition">
                    {swap.shifts.map((s) => <option key={s.id} value={s.id}>{fmtDateStr(s.date)} · {s.start_time}–{s.end_time}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-400 uppercase tracking-wider">Swap with</label>
                  <select data-testid="swap-coworker-select" value={swapTarget} onChange={(e) => setSwapTarget(e.target.value)}
                    className="mt-1.5 w-full rounded-xl bg-slate-950 border border-slate-700 px-4 py-3 focus:border-blue-500 outline-none transition">
                    <option value="">Choose a coworker…</option>
                    {swap.coworkers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              </div>
              <button onClick={submitSwap} data-testid="swap-submit-button"
                className="mt-6 w-full h-14 rounded-2xl text-white font-bold text-lg transition-transform active:scale-95" style={{ backgroundColor: accent }}>
                Send swap request
              </button>
              <button onClick={reset} data-testid="swap-cancel-button" className="mt-3 w-full text-sm text-slate-400 hover:text-slate-200 transition-colors">Cancel</button>
            </motion.div>
          )}

          {step === "done" && (
            <motion.div key="done" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center cs-pop">
              <div className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 border ${lastOffline ? "bg-amber-500/15 border-amber-500/40" : "bg-emerald-500/15 border-emerald-500/40"}`}>
                {lastOffline ? <WifiOff className="w-12 h-12 text-amber-400" /> : <CheckCircle2 className="w-12 h-12 text-emerald-400" />}
              </div>
              <h2 className="text-3xl font-extrabold" data-testid="clock-success">{lastOffline ? "Saved offline" : "All set!"}</h2>
              <p className="text-slate-400 mt-2">
                {lastOffline ? "Punch stored on this device — it will sync when you're back online." : `${employee?.name} · ${ACTION_META[lastAction]?.label || "Recorded"} at ${fmtTime(new Date())}`}
              </p>
              {isMobile && !lastOffline && geo && (
                <a data-testid="mobile-location-link" href={`https://www.google.com/maps?q=${geo.lat},${geo.lng}`} target="_blank" rel="noreferrer"
                  className="mt-3 inline-flex items-center gap-1.5 text-sm text-blue-400 hover:text-blue-300">
                  <MapPin className="w-4 h-4" /> Signed in at {geo.lat.toFixed(5)}, {geo.lng.toFixed(5)}
                </a>
              )}
              <button onClick={reset} className="mt-8 px-8 py-3 rounded-xl bg-slate-800 hover:bg-slate-700 font-semibold transition-colors" data-testid="clock-done-button">Done</button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {lockOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="kiosk-lock-modal">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 w-full max-w-xs text-center">
            <h3 className="font-bold text-lg mb-1">Enter exit PIN</h3>
            <p className="text-slate-400 text-sm mb-4">Ask a manager to unlock the kiosk.</p>
            <input autoFocus data-testid="kiosk-lock-pin-input" type="password" inputMode="numeric" value={lockPin}
              onChange={(e) => setLockPin(e.target.value.replace(/\D/g, ""))}
              onKeyDown={(e) => { if (e.key === "Enter") submitExit(); }}
              className="w-full text-center rounded-xl bg-slate-950 border border-slate-700 px-4 py-3 font-mono tracking-widest focus:border-blue-500 outline-none" />
            {lockSecs > 0 && <p data-testid="exit-lock-countdown" className="mt-3 text-sm text-amber-400 font-semibold">Too many attempts — try again in {lockSecs}s</p>}
            <div className="grid grid-cols-2 gap-3 mt-4">
              <button data-testid="kiosk-lock-cancel" onClick={() => setLockOpen(false)} className="rounded-xl bg-slate-800 hover:bg-slate-700 py-2.5 font-semibold">Cancel</button>
              <button data-testid="kiosk-lock-submit" onClick={submitExit} disabled={lockSecs > 0} className="rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-60 py-2.5 font-semibold text-white">Unlock</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
